import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomDatePicker from 'components/shared/CustomDatePicker/CustomDatePicker';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import dayjs, { Dayjs } from 'dayjs';
import { useFormikContext } from 'formik';
import { FieldConfig } from 'model/deals.model';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { SHARED_TOP_FIELDS } from 'utilities/constants';

interface BaseDealFormProps {
  fields: FieldConfig[];
}

const baseFields = (fields: FieldConfig[]) => {
  return [...SHARED_TOP_FIELDS, ...fields];
};

const BaseDealForm = ({ fields }: BaseDealFormProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const details = values.dealDetails || {};

  const [clientOptions, setClientOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientTotalLength, setClientTotalLength] = useState(0);

  const [ownerOptions, setOwnerOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [allOwnerOptions, setAllOwnerOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerTotalLength, setOwnerTotalLength] = useState(0);
  const initialOwnerOptions = useRef<any[]>([]);

  const formFields = useMemo(() => baseFields(fields), [fields]);

  const fetchClients = useCallback(async (searchText?: string) => {
    try {
      setClientLoading(true);
      const data = await DealsService.getAllClients(searchText);
      const clients = data?.clients || (Array.isArray(data) ? data : []);
      const formattedClients = clients
        .map((item: any) => ({
          Name: item.Name || item.name || item.clientName || '',
          Id: item.AccountId || item.Id || item.id || item.clientId || '',
        }))
        .filter((item: any) => item.Name && item.Id);
      setClientOptions(formattedClients);
      setClientTotalLength(formattedClients.length);
    } catch (error) {
      console.error('Failed to fetch clients', error);
      setClientOptions([]);
      setClientTotalLength(0);
    } finally {
      setClientLoading(false);
    }
  }, []);

  const fetchOwners = useCallback(async () => {
    try {
      setOwnerLoading(true);
      const data = await DealsService.getMembers();
      const members = data?.members || (Array.isArray(data) ? data : []);
      const formattedOwners = members
        .map((item: any) => ({
          Name: `${item.firstName} ${item.lastName}` || '',
          Id: item.cognitoSub || '',
        }))
        .filter((item: any) => item.Name && item.Id);
      setAllOwnerOptions(formattedOwners);
      setOwnerOptions(formattedOwners);
      setOwnerTotalLength(formattedOwners.length);
      initialOwnerOptions.current = members;
    } catch (error) {
      console.error('Failed to fetch owners', error);
      setAllOwnerOptions([]);
      setOwnerOptions([]);
      setOwnerTotalLength(0);
    } finally {
      setOwnerLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchOwners();
  }, [fetchClients, fetchOwners]);

  const handleClientSearch = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      fetchClients(value);
    },
    [fetchClients],
  );

  const handleOwnerSearch = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      if (!value) {
        setOwnerOptions(allOwnerOptions);
        return;
      }
      const filtered = allOwnerOptions.filter((owner) =>
        owner.Name.toLowerCase().includes(value.toLowerCase()),
      );
      setOwnerOptions(filtered);
    },
    [allOwnerOptions],
  );

  const renderField = (f: FieldConfig) => {
    const raw = details[f.name.replace('dealDetails.', '')];
    const value = raw ?? (f.type === 'number' ? '' : '');

    if (f.type === 'checkbox') {
      return (
        <FormControlLabel
          label={f.label}
          control={
            <Checkbox
              name={f.name}
              checked={Boolean(raw)}
              onChange={(e) =>
                handleChange({ target: { name: f.name, value: e.target.checked } } as any)
              }
              onBlur={handleBlur}
            />
          }
        />
      );
    }

    if (f.type === 'date') {
      let dateValue: Dayjs | null = null;
      if (raw) {
        if (dayjs.isDayjs && dayjs.isDayjs(raw)) {
          dateValue = raw;
        } else if (raw instanceof Date) {
          dateValue = dayjs(raw);
        } else if (typeof raw === 'string') {
          dateValue = dayjs(raw);
        } else {
          dateValue = dayjs(raw);
        }
      }

      const dateSetter = ((newValue: Dayjs | null | ((prev: Dayjs | null) => Dayjs | null)) => {
        const actualValue = typeof newValue === 'function' ? newValue(dateValue) : newValue;
        const formattedValue = actualValue ? actualValue.toDate() : null;
        setFieldValue(f.name, formattedValue);
      }) as React.Dispatch<React.SetStateAction<Dayjs | null>>;

      return <CustomDatePicker value={dateValue} setValue={dateSetter} label={f.label} />;
    }

    if (f.type === 'autocomplete' && f.autocompleteType) {
      const fieldName = f.name.replace('dealDetails.', '');
      const currentValue = details[fieldName];

      const selectedOption =
        f.autocompleteType === 'client'
          ? clientOptions.find((opt) => opt.Name === currentValue || opt.Id === currentValue)
          : ownerOptions.find((opt) => opt.Name === currentValue || opt.Id === currentValue);

      const options = f.autocompleteType === 'client' ? clientOptions : ownerOptions;
      const loading = f.autocompleteType === 'client' ? clientLoading : ownerLoading;
      const totalLength = f.autocompleteType === 'client' ? clientTotalLength : ownerTotalLength;
      const baseHandleFilter =
        f.autocompleteType === 'client' ? handleClientSearch : handleOwnerSearch;

      const handleFilter = (event: React.SyntheticEvent, value: string) => {
        if (selectedOption && value !== selectedOption.Name) {
          setFieldValue(f.name, '');
          if (f.autocompleteType === 'client') {
            setFieldValue('dealDetails.accountId', '');
          }
        }
        baseHandleFilter(event, value);
      };

      return (
        <CustomAutocomplete
          popupIcon='/assets/Search.svg'
          required={f.required}
          options={options}
          label={f.label}
          getOptionLabel={(option: any) => option?.Name || ''}
          handleDeleteItems={() => {
            setFieldValue(f.name, '');
            if (f.autocompleteType === 'client') {
              setFieldValue('dealDetails.accountId', '');
            }
          }}
          selectedItems={{}}
          handleSelection={(option: any) => {
            setFieldValue(f.name, option?.Name || '');
            if (f.autocompleteType === 'client' && option?.Id) {
              setFieldValue('dealDetails.accountId', option.Id);
            }
            if (f.autocompleteType === 'owner' && option?.Id) {
              setFieldValue(
                'dealDetails.owner',
                initialOwnerOptions.current.find((opt) => opt.cognitoSub === option.Id),
              );
            }
          }}
          value={selectedOption || null}
          multiple={false}
          handleFilter={handleFilter}
          placeholder={f.label}
          currentLength={options.length}
          totalLength={totalLength}
          next={() => {}}
          loading={loading}
        />
      );
    }

    if (f.type === 'select' && f.options) {
      return (
        <FormControl fullWidth sx={{ width: '100%', minWidth: 0 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{
              fontSize: isXs ? 14 : 16,
              '& .MuiFormLabel-asterisk': {
                color: zeplinColor.Error,
              },
            }}
            required={f.required}
          >
            {f.label}
          </InputLabel>
          <Select
            IconComponent={KeyboardArrowDown}
            required={f.required}
            name={f.name}
            value={value}
            label={f.label}
            onChange={handleChange}
            onBlur={handleBlur}
            size={isXs ? 'small' : 'medium'}
            sx={{
              width: '100%',
              minWidth: 0,
              '& .MuiSelect-select': {
                fontSize: isXs ? 14 : 16,
              },
            }}
            endAdornment={
              value && (
                <IconButton
                  size='small'
                  sx={{ marginRight: 2, visibility: value ? 'visible' : 'hidden' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChange({ target: { name: f.name, value: '' } });
                  }}
                >
                  <ClearIcon fontSize='small' />
                </IconButton>
              )
            }
          >
            {f.options.map((opt) => (
              <MenuItem key={String(opt)} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <CustomTextField
        required={f.required}
        style={{ width: '100%' }}
        name={f.name}
        label={f.label}
        type={f.type === 'number' ? 'number' : 'text'}
        text={value ?? ''}
        onChange={handleChange}
        onBlur={() => handleBlur(f.name as any)}
        multiline={f.multiline}
        form={true}
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '24px',
        width: '100%',
        paddingTop: '8px',
        overflow: 'scroll',
      }}
    >
      {formFields.map((f, i) => (
        <Box
          key={f.label + i}
          sx={{ gridColumn: { xs: 'span 12', sm: `span ${f.sm ?? f.xs ?? 12}` } }}
        >
          {renderField(f)}
        </Box>
      ))}
    </Box>
  );
};

export default BaseDealForm;
